import { ClockCircleOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { EDIT_EVENT_TIME } from 'api/graphql/v2/mutations/Event';
import {
  GET_CONVERSION_STATUS,
  GET_FULL_EVENT
} from 'api/graphql/v2/queries/Events';
import { Select } from 'api/sharedComponents/reactHookFormComponents';
import ModalSemantic from 'components/modalSemantic';
import { Button } from 'lib/ui';
import { IEvent } from 'model/v2';
import moment from 'moment';
import React, { useContext } from 'react';
import { useLazyQuery, useMutation } from 'react-apollo';
import { useApolloClient } from '@apollo/react-hooks';
import { useReactiveVar } from '@apollo/client';
import { toast } from 'react-toastify';
import { CONVERTED_APPOINTMENT_MESSAGE } from 'utils/downstreams/DownStreamModalConfigurations';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { Checkbox, Grid } from 'semantic-ui-react';
import {
  closeEditActualTime,
  editActualTimeEventIdVar,
  updateCalendarPref
} from 'utils/cache/calendar';
import { plannerContext } from 'pages/MainCalendarPage';
import { DEFAULT_EDIT_TIME_NOTE } from 'utils/constants/default';
import { APPOINTMENT_BY_MINUTE_VALIDATOR } from 'utils/validators/appointmentByMinute';
import {
  getEndOffset,
  getStartOffset,
  setOptions
} from 'views/components/calendar/mainCalendar/ModalEditTime';
import { FormItem, TextArea } from 'views/containers/form';
import { EditTimeModalWrapper } from './style';

interface Props {
  onClose: () => void;
  event: IEvent;
  doFullEventRequest: (val: any) => void;
  setVisible: any;
}
const EditTimeModal: React.FC<Props> = ({
  onClose,
  event,
  doFullEventRequest,
  setVisible
}: Props) => {
  return (
    <ModalSemantic
      open={true}
      onClose={onClose}
      trigger={<div></div>}
      content={
        <EditTimeForm
          event={event}
          onClose={onClose}
          doFullEventRequest={doFullEventRequest}
          setVisible={setVisible}
        />
      }
    />
  );
};
export default React.memo(EditTimeModal);

/**
 * Renders the Edit Appointment Time modal directly (without the appointment
 * sidebar) when triggered from the planner right-click context menu via
 * `editActualTimeEventIdVar`. Loads the full event first, since the modal needs
 * originalStartDate/originalEndDate which the calendar event does not carry.
 */
export const ContextMenuEditTimeModal: React.FC = () => {
  const eventId = useReactiveVar(editActualTimeEventIdVar);

  const [getFullEvent, { data }] = useLazyQuery(GET_FULL_EVENT, {
    fetchPolicy: 'network-only'
  });

  React.useEffect(() => {
    if (eventId) {
      getFullEvent({ variables: { id: eventId, canceled: false } });
    }
  }, [eventId, getFullEvent]);

  if (!eventId || data?.event?.id !== eventId) {
    return null;
  }

  return (
    <EditTimeModal
      event={data.event as IEvent}
      onClose={closeEditActualTime}
      setVisible={() => closeEditActualTime()}
      doFullEventRequest={() => {}}
    />
  );
};

const EditTimeForm: React.FC<Props> = ({
  event,
  onClose,
  doFullEventRequest,
  setVisible
}: Props) => {
  const { SetEditActualTimeState } = useContext(plannerContext);
  const methods = useForm({
    defaultValues: {
      startTimeOffset: getStartOffset(event.originalStartDate, event.startDate),
      endTimeOffset: getEndOffset(
        event.originalStartDate,
        event.startDate,
        event.endDate
      ),
      id: event.id,
      clinic: { timezone: event.clinic?.timezone },
      notes: DEFAULT_EDIT_TIME_NOTE,
      addAdminStart: false,
      addAdminEnd: false
    },
    resolver: yupResolver(APPOINTMENT_BY_MINUTE_VALIDATOR),
    mode: 'onChange'
  });

  const notes = useWatch({ name: 'notes', control: methods.control });
  const startTimeOffset = useWatch({
    name: 'startTimeOffset',
    control: methods.control
  });
  const endTimeOffset = useWatch({
    name: 'endTimeOffset',
    control: methods.control
  });
  const addAdminStart = useWatch({
    name: 'addAdminStart',
    control: methods.control
  });
  const addAdminEnd = useWatch({
    name: 'addAdminEnd',
    control: methods.control
  });

  // Start/end validity depends on each other (start < end), but RHF only
  // re-validates the field that just changed, leaving a stale error on the
  // other side. Re-trigger both whenever either one changes so a fix on one
  // side clears the error it caused on the other.
  // NOTE: depend on `trigger` itself (stable across renders), not the whole
  // `methods` object — that object's identity changes on every render, which
  // would re-run this effect after every trigger() call and loop forever.
  const { trigger } = methods;
  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    trigger(['startTimeOffset', 'endTimeOffset']);
  }, [startTimeOffset, endTimeOffset, trigger]);

  // Offsets as they were when the modal opened (i.e. the appointment's current
  // start/end). Admin time reflects only the change made in THIS edit session,
  // measured against these — not against the original appointment times.
  const initialStartOffset = React.useMemo(
    () => getStartOffset(event.originalStartDate, event.startDate),
    [event]
  );
  const initialEndOffset = React.useMemo(
    () => getEndOffset(event.originalStartDate, event.startDate, event.endDate),
    [event]
  );
  // Admin time is the gap freed up when the appointment is shortened in this
  // session (later start / earlier end). The minute value is display-only; the
  // checkbox controls whether the admin fill is created (skip flags below).
  const startAdminMinutes =
    (Number(startTimeOffset) || 0) - initialStartOffset;
  const endAdminMinutes = initialEndOffset - (Number(endTimeOffset) || 0);
  // A start/end validation error means the admin-time calculation above is
  // meaningless, so hide both checkboxes until the times are valid again.
  const hasTimeError = !!(
    methods.errors.startTimeOffset || methods.errors.endTimeOffset
  );
  // Show a side's admin option only when that side's time actually changed in
  // the direction that frees up time.
  const showStartAdmin = startAdminMinutes > 0 && !hasTimeError;
  const showEndAdmin = endAdminMinutes > 0 && !hasTimeError;

  // Display the admin block as the time range it covers (e.g. "09:15 am to
  // 09:22 am"), formatted the same way as the time dropdown options.
  const formatOffset = React.useCallback(
    (offset: number) =>
      moment(event.originalStartDate || event.startDate)
        .clone()
        .add(offset, 'minutes')
        .format('hh:mm a'),
    [event.originalStartDate, event.startDate]
  );
  // Start admin covers from the appointment's start when the modal opened to
  // the new (later) start. End admin covers from the new (earlier) end to the
  // end when the modal opened.
  const startAdminRange = `${formatOffset(initialStartOffset)} to ${formatOffset(
    Number(startTimeOffset) || 0
  )}`;
  const endAdminRange = `${formatOffset(
    Number(endTimeOffset) || 0
  )} to ${formatOffset(initialEndOffset)}`;

  const [startTimeOptions, setStartTimeOptions] = React.useState([]);
  const [endTimeOptions, setEndTimeOptions] = React.useState([]);
  const validOptions = React.useRef([]);
  React.useEffect(() => {
    setOptions(
      event.startDate,
      event.endDate,
      event.originalStartDate,
      event.originalEndDate,
      validOptions,
      setStartTimeOptions,
      setEndTimeOptions
    );
  }, [event]);
  const [saving, setSaving] = React.useState(false);
  const [editEventTime] = useMutation(EDIT_EVENT_TIME);
  // Re-check conversion status at save time. The sidebar/context menu only
  // checks when first opened, so a note submitted while this modal stays open
  // would otherwise let a converted appointment be rescheduled.
  const apolloClient = useApolloClient();
  const submitForm = React.useCallback(
    async (data: any) => {
      setSaving(true);
      try {
        const { data: conversionData } = await apolloClient.query({
          query: GET_CONVERSION_STATUS,
          variables: { id: event.id },
          fetchPolicy: 'network-only'
        });
        if (conversionData?.isConverted) {
          toast.error(CONVERTED_APPOINTMENT_MESSAGE);
          setSaving(false);
          // Refresh the sidebar so its actions reflect the now-converted state.
          doFullEventRequest({ variables: { id: event.id } });
          onClose();
          return;
        }
      } catch (error) {
        // If the check fails we fall through and let the edit attempt proceed,
        // surfacing any real error through the mutation's catch below.
      }
      const eventStart = moment(event.originalStartDate || event.startDate);
      const editedStart = eventStart
        .clone()
        .add(data.startTimeOffset, 'minutes');
      editEventTime({
        variables: {
          data: {
            event: {
              startDate: editedStart
                .seconds(0)
                .milliseconds(0)
                .toISOString(),
              endDate: eventStart
                .clone()
                .add(data.endTimeOffset, 'minutes')
                .seconds(0)
                .milliseconds(0)
                .toISOString(),
              id: event.id,
              clinic: { timezone: event?.clinic?.timezone }
            },
            notes: data.notes,
            // The per-side checkboxes drive the existing admin-fill flow.
            // checked => create admin direct for the freed time (skip = false)
            // unchecked / no gap => do not create (skip = true)
            skipStartAdminFill: !(showStartAdmin && data.addAdminStart),
            skipEndAdminFill: !(showEndAdmin && data.addAdminEnd)
          }
        }
      })
        .then(() => {
          setSaving(false);
          updateCalendarPref(editedStart);
          onClose();
          setVisible(false);
          SetEditActualTimeState(false);
          doFullEventRequest({
            variables: { id: event.id }
          });
        })
        .catch(() => {
          setSaving(false);
        });
    },
    [
      event.originalStartDate,
      event.startDate,
      event.id,
      editEventTime,
      apolloClient,
      onClose,
      setVisible,
      doFullEventRequest,
      showStartAdmin,
      showEndAdmin
    ]
  );
  const submitError = React.useCallback(() => {
    methods.trigger();
  }, [methods]);
  return (
    <EditTimeModalWrapper>
      <FormProvider {...methods}>
        <Controller name="id" control={methods.control} render={() => <></>} />

        <h2 className="edit-appointment-title">Edit Appointment Time</h2>

        <div>
          <Grid columns={2}>
            <Grid.Row>
              <Grid.Column className="start-time">
                <FormItem optional={false} label="Start Time">
                  <Controller
                    name="startTimeOffset"
                    control={methods.control}
                    render={fieldProps => (
                      <Select
                        suffixIcon={<ClockCircleOutlined />}
                        options={startTimeOptions}
                        field={fieldProps}
                        errors={methods.errors}
                      />
                    )}
                  />
                </FormItem>
                {showStartAdmin && (
                  <div className="admin-time-section">
                    <Controller
                      name="addAdminStart"
                      control={methods.control}
                      render={() => (
                        <Checkbox
                          className="admin-time-checkbox"
                          label={
                            <label>
                              Add admin time at the start{' '}
                              <span className="optional-text">(optional)</span>
                            </label>
                          }
                          checked={!!addAdminStart}
                          onChange={(_e, { checked }) =>
                            methods.setValue('addAdminStart', !!checked)
                          }
                        />
                      )}
                    />
                    {addAdminStart && (
                      <div className="admin-time-value">
                        <span className="admin-time-range">
                          {startAdminRange}
                        </span>
                        <span className="admin-time-label">admin time</span>
                      </div>
                    )}
                  </div>
                )}
              </Grid.Column>
              <Grid.Column className="end-time">
                <FormItem optional={false} label="End Time">
                  <Controller
                    name="endTimeOffset"
                    control={methods.control}
                    render={fieldProps => (
                      <Select
                        suffixIcon={<ClockCircleOutlined />}
                        options={endTimeOptions}
                        field={fieldProps}
                        errors={methods.errors}
                      />
                    )}
                  />
                </FormItem>
                {showEndAdmin && (
                  <div className="admin-time-section">
                    <Controller
                      name="addAdminEnd"
                      control={methods.control}
                      render={() => (
                        <Checkbox
                          className="admin-time-checkbox"
                          label={
                            <label>
                              Add admin time at the end{' '}
                              <span className="optional-text">(optional)</span>
                            </label>
                          }
                          checked={!!addAdminEnd}
                          onChange={(_e, { checked }) =>
                            methods.setValue('addAdminEnd', !!checked)
                          }
                        />
                      )}
                    />
                    {addAdminEnd && (
                      <div className="admin-time-value">
                        <span className="admin-time-range">{endAdminRange}</span>
                        <span className="admin-time-label">admin time</span>
                      </div>
                    )}
                  </div>
                )}
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>

        <div className="notes-wrapper">
          <FormItem optional={true} label="Notes">
            <Controller
              name="notes"
              control={methods.control}
              render={() => (
                <TextArea
                  name="notes"
                  placeholder="N/A"
                  value={notes}
                  onChange={(e: any) =>
                    methods.setValue('notes', e.target.value)
                  }
                  errors={methods.errors}
                />
              )}
            />
          </FormItem>
        </div>

        <div className="modal-actions">
          <Button
            className="cancel-btn"
            onClick={onClose}
            type=""
            loading={saving}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            className="ok-btn"
            onClick={methods.handleSubmit(submitForm, submitError)}
            type="submit"
            loading={saving}
            disabled={
              saving ||
              !methods.formState.isValid ||
              !methods.formState.isDirty
            }
          >
            Ok
          </Button>
        </div>
      </FormProvider>
    </EditTimeModalWrapper>
  );
};
